import clsx from 'clsx';

import { condition, fmtHour, fmtTemp, type HourData, type TemperatureUnit } from '../weather';
import WeatherIcon, { DropIcon } from './WeatherIcon';

interface Props {
  hours: HourData[];
  unit: TemperatureUnit;
  // Sample every nth hour so at most this many columns show, sharing the
  // width instead of scrolling. Without it the strip scrolls sideways.
  maxColumns?: number;
  className?: string;
}

// One column per hour: time, condition, temperature, and the chance of
// precipitation when there is one.
export default function HourlyStrip({ hours, unit, maxColumns, className }: Props) {
  if (hours.length === 0) return null;
  const nowHour = Math.floor(Date.now() / 3_600_000) * 3600;
  const step = maxColumns ? Math.ceil(hours.length / maxColumns) : 1;
  const shown = hours.filter((_, i) => i % step === 0);
  return (
    <div className={clsx('flex gap-[2px]', maxColumns ? 'overflow-hidden' : 'overflow-x-auto pb-1', className)}>
      {shown.map((h) => {
        const cond = condition(h.weather_code);
        const p = h.precipitation_probability;
        const isNow = h.t === nowHour;
        return (
          <div
            key={h.t}
            title={cond?.label}
            className={clsx(
              'flex flex-col items-center gap-[3px] rounded py-[5px] text-[0.7rem] leading-none',
              maxColumns ? 'min-w-0 flex-1' : 'w-[42px] flex-shrink-0',
              isNow ? 'bg-[var(--bg-hover)] text-[var(--text)]' : 'text-[var(--text-muted)]'
            )}
          >
            <span className="text-[var(--text-faint)]">{isNow ? 'Now' : fmtHour(h.t)}</span>
            {cond ? (
              <WeatherIcon kind={cond.icon} night={h.is_day === 0} size={16} />
            ) : (
              <span className="h-4" />
            )}
            <span className="tabular-nums text-[var(--text)]">{fmtTemp(h.temperature_2m, unit)}</span>
            <span className="flex h-[10px] items-center gap-[1px] tabular-nums text-[var(--text-faint)]">
              {p != null && p >= 10 && (
                <>
                  <DropIcon size={8} />
                  {Math.round(p)}%
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
